import { TOURNAMENT_GAME_RESULT } from "vue-chess-shared/constants";
import { initGames } from "vue-chess-shared/tournament/utils/gameUtils";

/**
 * Mixin that provides common round management functionality
 * for both AdminRoundTable and TournamentRoundDetails
 */
export default {
  data() {
    return {
      unsaved: false,
    };
  },

  methods: {
    /**
     * Show confirmation dialog for aborting round
     */
    showAbortConfirmation() {
      this.$buefy.dialog.confirm({
        title: `Reset Round ${this.round.roundNumber}?`,
        message: `Be careful! This action would ABORT all games in current round.`,
        cancelText: "Cancel",
        confirmText: "Abort",
        type: "is-danger",
        trapFocus: true,
        onConfirm: () => {
          this.handleAbortRound();
        },
      });
    },

    /**
     * Show confirmation dialog for resetting round
     */
    showResetConfirmation() {
      if (
        !this.roundGames ||
        this.roundGames.find(
          (g) => g.matchRef && g.matchRef.status !== "aborted"
        )
      ) {
        this.$buefy.toast.open({
          message: `There are still un-aborted games! ABORT all games in the round first`,
          type: "is-danger",
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: `Reset Round ${this.round.roundNumber}?`,
        message: `This action would unlink all existing round games from the tournament. All of them should have been aborted`,
        cancelText: "Cancel",
        confirmText: "Reset",
        type: "is-dark",
        trapFocus: true,
        onConfirm: () => {
          this.handleResetRound();
        },
      });
    },

    /**
     * Show confirmation dialog for pairing round
     */
    showPairConfirmation() {
      if (!this.roundGames || this.roundGames.find((g) => g.matchRef)) {
        this.$buefy.toast.open({
          message: `There are round game(s) which have started. You have to RESET the round before re-pairing`,
          type: "is-danger",
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: `${this.tournament.type} pair Round ${this.round.roundNumber}`,
        message: `This action do nothing if there are existing games. If that is the case, you have to ABORT those games first.`,
        cancelText: "Cancel",
        confirmText: "Pair",
        type: "is-primary",
        trapFocus: true,
        onConfirm: async () => {
          this.handlePairRound();
        },
      });
    },

    /**
     * Handle abort round action
     */
    handleAbortRound() {
      this.$emit("onAbort", this.round);
    },

    /**
     * Handle reset round action
     */
    handleResetRound() {
      this.roundGames = initGames(
        this.round.roundNumber,
        Math.ceil(this.players.length / 2)
      );
      this.unsaved = true;
      this.$buefy.toast.open({
        message: `round ${this.round.roundNumber} games were reset. If this was an accident, DON'T SAVE`,
        type: "is-danger",
      });
      this.$emit("onReset", this.round);
    },

    /**
     * Handle pair round action
     */
    handlePairRound() {
      this.$emit("onPair", this.round);
      this.unsaved = false;
    },

    /**
     * Handle revert round action
     */
    handleRevertRound() {
      this.unsaved = false;
      if (this.initRoundGames) {
        this.initRoundGames();
      }
    },

    /**
     * Handle save round action
     */
    handleSaveRound() {
      this.$emit("onSave", this.round, this.roundGames);
    },

    /**
     * Handle start games action (migrated from TournamentRound.vue)
     */
    handleStartGames() {
      this.$emit("onStart", this.round);
    },

    /**
     * Handle start unstarted games action
     */
    handleStartUnstartedGames() {
      this.$emit("onStartUnstartedGames", this.round);
    },

    /**
     * Handle fix results action
     */
    handleFixResults() {
      this.$emit("onFixResults", this.round);
    },

    /**
     * Handle finish round action
     */
    handleFinishRound() {
      this.$emit("onFinish", this.round);
    },

    /**
     * Handle unfinish round action
     */
    handleUnfinishRound() {
      this.$emit("onUnfinish", this.round);
    },

    /**
     * Handle copy round action
     */
    handleCopyRound() {
      this.$emit("onCopy", this.round);
    },

    /**
     * Handle simulate round action
     */
    handleSimulateRound() {
      this.$emit("onSimulate", this.round);
    },

    /**
     * Handle clear results action
     */
    handleClearResults() {
      this.roundGames.forEach((g) => {
        g.whiteResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
        g.blackResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
      });
    },

    /**
     * Handle start game action
     */
    handleStartGame(game) {
      this.$emit("onStartGame", this.round, game);
    },

    /**
     * Handle rematch game action
     */
    handleRematchGame(game) {
      this.$emit("onRematchGame", this.round, game);
    },

    /**
     * Handle round games updated action
     */
    handleRoundGamesUpdated(newRoundGames) {
      this.roundGames = [...newRoundGames];
    },
  },
};
